<template>
  <EmailPasswordLoginDialog
    region="cn"
    :visible="pageVisible"
    :handle-ok="props.handleOk"
    :remove="props.remove"
    :right-type="props.rightType"
    :create_type="props.create_type"
    :source="props.source"
    :signup_source="props.signup_source"
    :initial-email="props.initialEmail"
    :submit-phone-code="submitPhoneCode"
    :send-phone-verify-code="sendPhoneVerifyCode"
    :submit-email-password="submitEmailPassword"
  />
</template>

<script setup>
/* eslint-disable vue/prop-name-casing */
import { ref } from "vue";

import apiUser from "@/api/api/apiUser.js";
import { getLatestUtmSourceFromCookie } from "@/api/net/config.js";
import message from "@/components/functionCallComponent/message/message.js";
import EmailPasswordLoginDialog from "@/components/functionComp/components/EmailPasswordLoginDialog.vue";
import useUserDataStore from "@/store/userDataStore";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  handleOk: {
    type: Function,
    default: null,
  },
  remove: {
    type: Function,
    default: null,
  },
  rightType: {
    type: String,
    default: "login",
  },
  create_type: {
    type: String,
    default: "2",
  },
  source: {
    type: String,
    default: "other",
  },
  signup_source: {
    type: String,
    default: "",
  },
  initialEmail: {
    type: String,
    default: "",
  },
});

const pageVisible = ref(props.visible);
const userDataStore = useUserDataStore();

const showError = (content) => {
  message.error({
    maskClosable: true,
    position: "top",
    content,
  });
};

const updateAuthData = (_data) => {
  userDataStore.updateAuthData(_data);
};

const sendPhoneVerifyCode = async (phone) => {
  try {
    const { code, msg, block_end_time } = await apiUser.getVerifyCode({
      phone,
    });

    switch (code) {
      case 200:
        return true;
      case 1019:
        if (block_end_time === 5000000000) {
          showError("该账户已被永久封禁，无法登录。如有疑问，请联系客服。");
        } else {
          const blockEndDate = new Date(block_end_time * 1000);
          const formattedDate = `${blockEndDate.getFullYear()}年${
            blockEndDate.getMonth() + 1
          }月${blockEndDate.getDate()}日`;
          showError(
            `该账户已被封禁至${formattedDate}，暂时无法登录。如有疑问，请联系客服。`
          );
        }
        return false;
      case 3002:
      case 3003:
        showError("发送过于频繁，请稍后再试");
        return false;
      default:
        showError(msg || "获取验证码失败，请稍后重试");
        return false;
    }
  } catch (error) {
    console.error("验证码请求失败:", error);
    showError("网络错误，请稍后重试");
    return false;
  }
};

const submitPhoneCode = async ({ account, password }) => {
  const { data, code, msg, block_end_time } = await apiUser.userLogin({
    token: account,
    mode: "otp_message",
    extra_token: password,
  });

  if (code === 200) {
    updateAuthData(data);
    if (typeof props.handleOk === "function") {
      props.handleOk(true);
    }
    pageVisible.value = false;
    userDataStore.getUserCertificationState();
    return true;
  }

  if (code === 3004) {
    return {
      fieldErrors: {
        password: "验证码错误",
      },
    };
  }

  if (code === 3005 || code === 3006) {
    return {
      fieldErrors: {
        account: "手机号格式不正确",
      },
    };
  }

  if (code === 3002 || code === 3003) {
    showError("发送过于频繁，请稍后再试");
  } else if (code === 2110) {
    showError("您的账号已注销");
  } else if (code === 1019) {
    if (block_end_time === 5000000000) {
      showError("该账户已被永久封禁，无法登录。如有疑问，请联系客服。");
    } else {
      const blockEndDate = new Date(block_end_time * 1000);
      const formattedDate = `${blockEndDate.getFullYear()}年${
        blockEndDate.getMonth() + 1
      }月${blockEndDate.getDate()}日`;
      showError(
        `该账户已被封禁至${formattedDate}，暂时无法登录。如有疑问，请联系客服。`
      );
    }
  } else {
    showError(msg || "登录失败，请稍后重试");
  }
  return false;
};

const submitEmailPassword = async ({
  account,
  password,
  intent = "signin",
}) => {
  const response = await apiUser.emailPasswordLogin({
    mode: intent === "signin" ? "email" : "otp_email",
    email: account,
    password,
    source: getLatestUtmSourceFromCookie() ?? "",
    signup_source: userDataStore.getSignupSource(props.signup_source),
    activity_source: userDataStore.getActivitySource(),
  });

  const registrationErrors = {
    9016: "该邮箱已注册，请登录。",
    9021: "该账号已存在，请输入邮箱地址和密码登录。",
  };

  const sendVerificationEmail = async () => {
    const verifyRes = await apiUser.requestEmailVerification({
      email: account,
      source: "web",
    });
    if (verifyRes?.code !== 200) {
      showError(
        registrationErrors[verifyRes?.code] || "验证邮件发送失败，请稍后重试。"
      );
      return false;
    }
    return { sentVerification: true };
  };

  if (response?.code === 200 && intent === "signup") {
    const hasLoginData = Boolean(
      response?.data?.auth || response?.data?.session_token
    );
    if (!hasLoginData) {
      return sendVerificationEmail();
    }

    return {
      fieldErrors: {
        account: "该邮箱已注册，请登录。",
      },
    };
  }

  if (response?.code === 200) {
    updateAuthData(response.data);
    if (typeof props.handleOk === "function") {
      props.handleOk(true);
    }
    pageVisible.value = false;
    userDataStore.getUserCertificationState();
    return true;
  }

  if (registrationErrors[response?.code]) {
    return {
      fieldErrors: {
        account: registrationErrors[response.code],
      },
    };
  }

  if (
    response?.code === 404 ||
    response?.code === 3008 ||
    response?.code === 9008 ||
    response?.code === 9017 ||
    response?.code === 9025 ||
    response?.code === 9023
  ) {
    if (intent !== "signup") {
      return {
        fieldErrors: {
          account: "未找到账号，请注册。",
        },
      };
    }
    return sendVerificationEmail();
  }

  if (intent === "signin" && response?.code === 9022) {
    return {
      fieldErrors: {
        password: response?.msg || "密码错误",
      },
    };
  }

  if (response?.code === 4001 || response?.code === 3004) {
    return {
      fieldErrors: {
        [intent === "signup" ? "account" : "password"]:
          intent === "signup" ? "该邮箱已注册，请登录。" : "密码错误",
      },
    };
  }

  if (intent === "signup") {
    return {
      blocked: /邮箱地址无效|一次性|临时邮箱|invalid email address/i.test(
        response?.msg || ""
      ),
      isNewUser: Boolean(response?.data?.is_new_user ?? response?.is_new_user),
      fieldErrors: {
        account: "邮箱地址无效，请更换后重试。",
      },
    };
  }

  showError("登录失败，请稍后重试。");
  return false;
};

defineExpose({ remove: props.remove });
</script>
